import { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Plus, Search } from 'lucide-react';
import { toast } from 'sonner';
import { PageHeader } from '@/shared/components/page-header';
import { useConfirm } from '@/shared/components/confirm';
import { Button } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';
import { Spinner } from '@/shared/ui/spinner';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/shared/ui/table';
import {
  deleteStoreUser,
  fetchStoreUsers,
  type StoreUser,
} from '../api/use-store-users';
import { StoreUserFormDialog } from './store-user-form-dialog';

export function StoreUsersPage() {
  const { idPassbook = '' } = useParams<{ idPassbook: string }>();
  const confirm = useConfirm();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editUser, setEditUser] = useState<StoreUser | null>(null);
  const [search, setSearch] = useState('');

  const { data = [], isLoading, refetch } = useQuery({
    queryKey: ['store-users', idPassbook],
    queryFn: () => fetchStoreUsers(idPassbook),
    enabled: Boolean(idPassbook),
  });

  const rows = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return data;
    return data.filter(
      (u) =>
        (u.profile?.name ?? '').toLowerCase().includes(term) ||
        (u.email ?? '').toLowerCase().includes(term),
    );
  }, [data, search]);

  function openCreate() {
    setEditUser(null);
    setDialogOpen(true);
  }

  function openEdit(user: StoreUser) {
    setEditUser(user);
    setDialogOpen(true);
  }

  async function handleDelete(user: StoreUser) {
    const name = user.profile?.name || user.email || 'este usuário';
    // A API apaga o documento (removeData=true), igual ao dash legado — por isso
    // a confirmação diz que é definitivo, em vez do "tem certeza?" genérico.
    const ok = await confirm({
      title: `Remover ${name}?`,
      description:
        'O usuário será apagado em definitivo e perderá o acesso ao painel. Não é possível desfazer.',
      confirmLabel: 'Remover',
      variant: 'destructive',
    });
    if (!ok) return;

    try {
      await deleteStoreUser(idPassbook, user._id);
      toast.success('Usuário removido.');
      void refetch();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Falha ao remover o usuário.');
    }
  }

  return (
    <div>
      <PageHeader
        title="Usuários"
        description="Operadores com acesso à tela de validação desta loja."
        help="Estes usuários têm perfil de operador: entram no painel apenas para dar selos e pontos aos clientes."
        actions={
          <Button onClick={openCreate}>
            <Plus className="mr-1 size-4" />
            Adicionar usuário
          </Button>
        }
      />

      <div className="mb-4 relative max-w-sm">
        <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Buscar por nome ou email"
          className="pl-9"
        />
      </div>

      {isLoading ? (
        <div className="flex justify-center py-16">
          <Spinner />
        </div>
      ) : rows.length === 0 ? (
        <p className="py-16 text-center text-sm text-muted-foreground">
          {data.length === 0
            ? 'Nenhum usuário cadastrado nesta loja.'
            : 'Nenhum usuário encontrado para essa busca.'}
        </p>
      ) : (
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nome</TableHead>
                <TableHead>Email</TableHead>
                <TableHead className="text-right">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((user) => (
                <TableRow key={user._id}>
                  <TableCell>{user.profile?.name ?? '—'}</TableCell>
                  <TableCell>{user.email}</TableCell>
                  <TableCell>
                    <div className="flex justify-end gap-2">
                      <Button variant="outline" size="sm" onClick={() => openEdit(user)}>
                        Editar
                      </Button>
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => void handleDelete(user)}
                      >
                        Remover
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <StoreUserFormDialog
        idPassbook={idPassbook}
        user={editUser}
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        onSaved={() => void refetch()}
      />
    </div>
  );
}
